import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save, X, Upload, Camera, Package, Tag, DollarSign, 
  Layers, Info, CheckCircle, AlertTriangle, Plus, Trash2
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSupabase } from '../../contexts/SupabaseContext';
import { createProduct } from '../../hooks/useProducts';
import { useCategories } from '../../hooks/useCategories';

// Function to upload product image to Supabase Storage
const uploadProductImage = async (file: File): Promise<string | null> => {
  try {
    // Get the current session to ensure we're authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('User not authenticated');
    }

    // Create a unique filename to prevent conflicts
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
    const filePath = `${session.user.id}/${fileName}`;
    
    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('product-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) throw error;
    
    // Get the public URL for the uploaded file
    const { data: { publicUrl } } = supabase.storage
      .from('product-images')
      .getPublicUrl(data.path);
    
    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    return null;
  }
};
const SellerAddProduct = () => {
  const navigate = useNavigate();
  const { user } = useSupabase();
  const { categories, loading: categoriesLoading } = useCategories();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    sku: '',
    price: '',
    original_price: '',
    description: '',
    category_id: '',
    subcategory_id: '',
    features: [''],
    in_stock: true,
    stock_quantity: '0',
    status: 'draft',
    badge: '',
    image_url: ''
  });

  // Quantity-based pricing tiers
  const [quantityPricing, setQuantityPricing] = useState([
    { min_quantity: 1, price: '' },
    { min_quantity: 50, price: '' },
    { min_quantity: 100, price: '' }
  ]);

  // Get supplier ID for the current user
  useEffect(() => {
    const getSupplierInfo = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('suppliers')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) throw error;
        
        if (data) {
          setSupplierId(data.id);
        }
      } catch (err) {
        console.error('Error fetching supplier info:', err);
        setError('Failed to fetch supplier information. Please try again later.');
      }
    };
    
    getSupplierInfo();
  }, [user]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.category_id && categories.length > 0) {
      const selectedCategory = categories.find(cat => cat.id === formData.category_id);
      if (selectedCategory && selectedCategory.subcategories) {
        setSubcategories(selectedCategory.subcategories);
      } else {
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id, categories]);

  // Generate slug from name
  useEffect(() => {
    if (formData.name) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.name]);

  // Update quantity pricing when price changes
  useEffect(() => {
    if (formData.price) {
      const basePrice = parseFloat(formData.price);
      if (!isNaN(basePrice)) {
        setQuantityPricing([
          { min_quantity: 1, price: basePrice.toFixed(2) },
          { min_quantity: 50, price: (basePrice * 0.9).toFixed(2) },
          { min_quantity: 100, price: (basePrice * 0.8).toFixed(2) }
        ]);
      }
    }
  }, [formData.price]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'price' || name === 'original_price' || name === 'stock_quantity') {
      // Only allow numbers and decimal point for price fields
      if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle feature changes
  const handleFeatureChange = (index: number, value: string) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: updatedFeatures }));
  };

  // Add a new feature field
  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  // Remove a feature field
  const removeFeature = (index: number) => {
    if (formData.features.length > 1) {
      const updatedFeatures = [...formData.features];
      updatedFeatures.splice(index, 1);
      setFormData(prev => ({ ...prev, features: updatedFeatures }));
    }
  };

  // Handle quantity pricing changes
  const handleQuantityPriceChange = (index: number, field: 'min_quantity' | 'price', value: string) => {
    // Only allow numbers and decimal point for price fields
    if (field === 'price' && value !== '' && !/^[0-9]*\.?[0-9]*$/.test(value)) {
      return;
    }
    
    // Only allow numbers for min_quantity
    if (field === 'min_quantity' && value !== '' && !/^[0-9]*$/.test(value)) {
      return;
    }
    
    const updatedPricing = [...quantityPricing];
    if (field === 'min_quantity') {
      updatedPricing[index].min_quantity = parseInt(value) || 1;
    } else {
      updatedPricing[index].price = value;
    }
    setQuantityPricing(updatedPricing);
  };

  // Add a new quantity pricing tier
  const addQuantityPricingTier = () => {
    const lastTier = quantityPricing[quantityPricing.length - 1];
    const newMinQuantity = lastTier.min_quantity * 2;
    const newPrice = parseFloat(lastTier.price) * 0.9;
    
    setQuantityPricing([
      ...quantityPricing,
      { 
        min_quantity: newMinQuantity, 
        price: !isNaN(newPrice) ? newPrice.toFixed(2) : '' 
      }
    ]);
  };

  // Remove a quantity pricing tier
  const removeQuantityPricingTier = (index: number) => {
    if (quantityPricing.length > 1) {
      const updatedPricing = [...quantityPricing];
      updatedPricing.splice(index, 1);
      setQuantityPricing(updatedPricing);
    }
  };

  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      // Show a temporary preview while uploading
      const objectUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        image_url: objectUrl
      }));
      
      // Upload the file to Supabase Storage
      const publicUrl = await uploadProductImage(file);
      
      if (publicUrl) {
        // Update the form data with the public URL
        setFormData(prev => ({
          ...prev,
          image_url: publicUrl
        }));
      } else {
        // If upload failed, revert to no image
        setFormData(prev => ({
          ...prev,
          image_url: ''
        }));
        setError('Failed to upload image. Please try again.');
      }
    } catch (err) {
      console.error('Error handling image upload:', err);
      setError('Failed to upload image. Please try again.');
      setFormData(prev => ({
        ...prev,
        image_url: ''
      }));
    }
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!supplierId) {
      setError('Supplier information not found. Please try again later.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Validate required fields
      if (!formData.name || !formData.price || !formData.category_id) {
        setError('Please fill in all required fields.');
        setIsSubmitting(false);
        return;
      }
      
      // Prepare product data
      const productData = {
        name: formData.name,
        slug: formData.slug,
        sku: formData.sku || null,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        description: formData.description,
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || null,
        supplier_id: supplierId,
        features: formData.features.filter(f => f.trim() !== ''),
        in_stock: formData.in_stock,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        status: formData.status,
        badge: formData.badge || null,
        image_url: formData.image_url || null,
        quantity_prices: quantityPricing.map(tier => ({
          min_quantity: tier.min_quantity,
          price: parseFloat(tier.price) || parseFloat(formData.price)
        }))
      };
      
      // Create the product
      const result = await createProduct(productData);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // Show success message
      setSuccess(true);
      
      // Redirect to products page after a delay
      setTimeout(() => {
        navigate('/seller-central/products');
      }, 2000);
      
    } catch (err) {
      console.error('Error creating product:', err);
      setError(err instanceof Error ? err.message : 'Failed to create product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add New Product</h1>
              <p className="text-gray-600">Create a new product listing</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/seller-central/products')}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Product</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span>Product created successfully! Redirecting to products page...</span>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Product Name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter product name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
                      Slug (URL)
                    </label>
                    <input
                      id="slug"
                      name="slug"
                      type="text"
                      value={formData.slug}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-gray-50"
                      placeholder="product-url-slug"
                      readOnly
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Auto-generated from product name
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-2">
                      SKU
                    </label>
                    <input
                      id="sku"
                      name="sku"
                      type="text"
                      value={formData.sku}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="e.g., CT-001"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                      Price *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">$</span>
                      </div>
                      <input
                        id="price"
                        name="price"
                        type="text"
                        required
                        value={formData.price}
                        onChange={handleChange}
                        className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="original_price" className="block text-sm font-medium text-gray-700 mb-2">
                      Original Price (Optional)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">$</span>
                      </div>
                      <input
                        id="original_price"
                        name="original_price"
                        type="text"
                        value={formData.original_price}
                        onChange={handleChange}
                        className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      For showing discounted prices
                    </p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Describe your product..."
                    />
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Categories</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      id="category_id"
                      name="category_id"
                      required
                      value={formData.category_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Select a category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="subcategory_id" className="block text-sm font-medium text-gray-700 mb-2">
                      Subcategory
                    </label>
                    <select
                      id="subcategory_id"
                      name="subcategory_id"
                      value={formData.subcategory_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={!formData.category_id || subcategories.length === 0}
                    >
                      <option value="">Select a subcategory</option>
                      {subcategories.map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Product Features</h2>
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Feature
                  </button>
                </div>
                
                <div className="space-y-3">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder={`Feature ${index + 1}`}
                      />
                      {formData.features.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Quantity-Based Pricing */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Quantity-Based Pricing</h2>
                  <button
                    type="button"
                    onClick={addQuantityPricingTier}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Tier
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Minimum Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price Per Unit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {quantityPricing.map((tier, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <input
                              type="text"
                              value={tier.min_quantity}
                              onChange={(e) => handleQuantityPriceChange(index, 'min_quantity', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              placeholder="Minimum quantity"
                              disabled={index === 0} // First tier is always 1
                            />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500">$</span>
                              </div>
                              <input
                                type="text"
                                value={tier.price}
                                onChange={(e) => handleQuantityPriceChange(index, 'price', e.target.value)}
                                className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="0.00"
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => removeQuantityPricingTier(index)}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <p className="mt-4 text-sm text-gray-500 flex items-center">
                  <Info className="h-4 w-4 mr-1 text-blue-500" />
                  Set different prices based on order quantity. The first tier is always the base price.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Product Image */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Image</h2>
                
                <div className="mb-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    {formData.image_url ? (
                      <div className="relative">
                        <img 
                          src={formData.image_url} 
                          alt="Product preview" 
                          className="w-full h-48 object-contain rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                          className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="py-8">
                        <Package className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No image uploaded</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Upload Image</span>
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  <button
                    type="button"
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Camera className="h-4 w-4" />
                    <span>Take Photo</span>
                  </button>
                </div>
                
                <p className="mt-2 text-xs text-gray-500">
                  Recommended: 800x800px, JPEG or PNG, max 5MB
                </p>
              </div>

              {/* Inventory */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Inventory</h2>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="stock_quantity" className="block text-sm font-medium text-gray-700 mb-2">
                      Stock Quantity
                    </label>
                    <input
                      id="stock_quantity"
                      name="stock_quantity"
                      type="text"
                      value={formData.stock_quantity}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="in_stock"
                      name="in_stock"
                      type="checkbox"
                      checked={formData.in_stock}
                      onChange={(e) => setFormData(prev => ({ ...prev, in_stock: e.target.checked }))}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="in_stock" className="ml-2 block text-sm text-gray-900">
                      In Stock
                    </label>
                  </div>
                </div>
              </div>

              {/* Product Status */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Status</h2>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="low_stock">Low Stock</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="badge" className="block text-sm font-medium text-gray-700 mb-2">
                      Badge (Optional)
                    </label>
                    <input
                      id="badge"
                      name="badge"
                      type="text"
                      value={formData.badge}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="e.g., New, Bestseller, Sale"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Display a badge on the product card
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Button (Mobile) */}
              <div className="bg-white rounded-lg shadow-sm p-6 lg:hidden">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save Product</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellerAddProduct;